import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, Loader2, Plus, Search, ShieldCheck } from 'lucide-react';
import { useEffect, useState } from 'react';

import { ADMIN_STATUS_LABELS, AdminStatusBadge } from '@/components/admins/AdminStatusBadge';
import { Page, PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FormAlert } from '@/components/ui/form-alert';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ADMIN_STATUSES, adminKeys, adminsApi, type AdminStatus } from '@/lib/admins-api';
import { genericErrorMessage } from '@/lib/api-errors';
import { formatDateTime, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { BACKUP_CODES_LOW, BACKUP_CODES_TOTAL } from '@/routes/settings';
import { DEFAULT_PAGE_SIZE, PAGE_SIZES, SEARCH_DEBOUNCE_MS } from '@/routes/tenants';
import { useAuthStore } from '@/stores/auth';

/** Filtres dans l'URL, comme /tenants. */
export interface AdminsSearch {
  page?: number;
  pageSize?: number;
  status?: AdminStatus;
  search?: string;
}

export const validateAdminsSearch = (search: Record<string, unknown>): AdminsSearch => {
  const page = Number(search['page']);
  const pageSize = Number(search['pageSize']);
  const status = search['status'];
  const text = typeof search['search'] === 'string' ? search['search'].trim().slice(0, 100) : '';
  return {
    ...(Number.isInteger(page) && page > 1 && { page }),
    ...((PAGE_SIZES as readonly number[]).includes(pageSize) && pageSize !== DEFAULT_PAGE_SIZE && { pageSize }),
    ...(ADMIN_STATUSES.includes(status as AdminStatus) && { status: status as AdminStatus }),
    ...(text && { search: text }),
  };
};

const COLUMNS = ['Email', 'Nom', 'Statut', 'Dernière connexion', 'Codes de secours', 'Actions'];

const selectClass =
  'h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

export const AdminsPage = () => {
  const navigate = useNavigate({ from: '/admins' });
  const { page = 1, pageSize = DEFAULT_PAGE_SIZE, status, search } = useSearch({ from: '/authed/admins' });
  const selfId = useAuthStore((s) => s.admin?.id);
  const [searchInput, setSearchInput] = useState(search ?? '');

  const setFilters = (patch: AdminsSearch, replace = false) =>
    void navigate({ search: (prev) => validateAdminsSearch({ ...prev, ...patch }), replace });

  // Saisie → URL après 300 ms sans frappe ; retour à la page 1 à chaque nouveau terme
  useEffect(() => {
    const value = searchInput.trim();
    if (value === (search ?? '')) return;
    const timer = setTimeout(() => setFilters({ search: value || undefined, page: undefined }, true), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // Seul le texte saisi relance le minuteur (search et navigate volontairement hors dépendances)
  }, [searchInput]);

  const params = { page, pageSize, ...(status && { status }), ...(search && { search }) };
  const query = useQuery({
    queryKey: adminKeys.list(params),
    queryFn: () => adminsApi.list(params),
    placeholderData: keepPreviousData,
  });

  const result = query.data;
  const filtered = Boolean(status || search);

  return (
    <Page>
      <PageHeader
        title="Admins"
        description="Administrateurs de la plateforme, du plus ancien au plus récent."
        actions={
          <Link to="/admins/new" className={buttonVariants()}>
            <Plus className="mr-2 h-4 w-4" aria-hidden />
            Créer un admin
          </Link>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Rechercher par email ou nom"
            aria-label="Rechercher par email ou nom"
            className="pl-9"
            maxLength={100}
          />
        </div>
        <select
          aria-label="Filtrer par statut"
          value={status ?? ''}
          onChange={(e) => setFilters({ status: (e.target.value || undefined) as AdminStatus | undefined, page: undefined })}
          className={selectClass}
        >
          <option value="">Tous les statuts</option>
          {ADMIN_STATUSES.map((s) => (
            <option key={s} value={s}>
              {ADMIN_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
      </div>

      {query.isError ? (
        <div className="space-y-3">
          <FormAlert>Impossible de charger les admins : {genericErrorMessage(query.error).toLowerCase()}</FormAlert>
          <Button variant="outline" onClick={() => void query.refetch()}>
            Réessayer
          </Button>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader className="bg-muted/50">
              <TableRow>
                {COLUMNS.map((c, i) => (
                  <TableHead key={c} className={i >= 4 ? 'text-right' : undefined}>
                    {c}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.isPending &&
                Array.from({ length: 3 }, (_, i) => (
                  <TableRow key={i} data-testid="admin-row-skeleton">
                    {COLUMNS.map((c) => (
                      <TableCell key={c}>
                        <Skeleton className="h-4 w-full max-w-[8rem]" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              {result?.data.map((a) => {
                const name = [a.firstName, a.lastName].filter(Boolean).join(' ');
                return (
                  <TableRow
                    key={a.id}
                    data-testid="admin-row"
                    className="cursor-pointer"
                    onClick={() => void navigate({ to: '/admins/$id', params: { id: a.id } })}
                  >
                    <TableCell className="font-medium">
                      {/* Lien réel pour le clavier, le clic milieu et les lecteurs d'écran ; la ligne entière reste cliquable */}
                      <Link to="/admins/$id" params={{ id: a.id }} className="hover:underline" onClick={(e) => e.stopPropagation()}>
                        {a.email}
                      </Link>
                      {a.id === selfId && (
                        <Badge variant="muted-soft" className="ml-2">
                          Vous
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{name || '—'}</TableCell>
                    <TableCell>
                      <AdminStatusBadge status={a.status} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{formatDateTime(a.lastLoginAt)}</TableCell>
                    <TableCell
                      className={cn('text-right tabular-nums', a.mfaBackupCodesCount <= BACKUP_CODES_LOW && 'text-amber-700')}
                    >
                      {a.mfaBackupCodesCount} / {BACKUP_CODES_TOTAL}
                    </TableCell>
                    <TableCell className="text-right">
                      <Link
                        to="/admins/$id"
                        params={{ id: a.id }}
                        className={buttonVariants({ variant: 'ghost', size: 'sm' })}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Gérer ${a.email}`}
                      >
                        Gérer
                        <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {result && result.data.length === 0 && (
            <div className="flex flex-col items-center gap-2 px-4 py-12 text-center text-sm text-muted-foreground">
              <ShieldCheck className="h-8 w-8 text-muted-foreground/50" aria-hidden />
              {filtered ? 'Aucun admin ne correspond à ces filtres.' : 'Aucun admin.'}
            </div>
          )}
        </Card>
      )}

      {result && result.total > 0 && (
        <div className="flex flex-col gap-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p data-testid="admins-range">
            {formatNumber((result.page - 1) * result.pageSize + 1)}–{formatNumber((result.page - 1) * result.pageSize + result.data.length)} sur{' '}
            {formatNumber(result.total)} admin{result.total > 1 ? 's' : ''}
            {query.isFetching && !query.isPending && <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin" aria-label="Chargement" />}
          </p>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2">
              Par page
              <select
                value={pageSize}
                onChange={(e) => setFilters({ pageSize: Number(e.target.value), page: undefined })}
                className={`${selectClass} h-9`}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setFilters({ page: page - 1 })} aria-label="Page précédente">
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Button>
            <span className="tabular-nums">
              Page {result.page} / {result.totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= result.totalPages}
              onClick={() => setFilters({ page: page + 1 })}
              aria-label="Page suivante"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
      )}
    </Page>
  );
};
